import { Close, Download, FitToScreen } from '@carbon/icons-react'
import { FC, useEffect, useState } from 'react'
import {
  FaFileAlt,
  FaFileArchive,
  FaFileAudio,
  FaFileCode,
  FaFileCsv,
  FaFileExcel,
  FaFileImage,
  FaFilePdf,
  FaFilePowerpoint,
  FaFileVideo,
  FaFileWord
} from 'react-icons/fa'

import { DDSTypography } from 'den-design-system'
import DocumentPreviewModal from '../../components/common/TextAreaDrafts/DocumentPreviewModal'
import React from 'react'
import Tooltip from '../../components/New/Tooltip/Tooltip'
import pallete from '../../global/newPallete'
import { useStoreContext } from '../../store/StoreContext'

type FilePreviewCardProps = {
  file: {
    name: string
    type: string
    url: string
    size: any
  }
  onClose?: () => void
  uploadType?: 'create' | 'display' | 'delete'
  activity?: 'activity'
  download?: any
}

const FilePreviewCard: FC<FilePreviewCardProps> = ({
  file,
  onClose,
  uploadType = 'display',
  activity,
  download
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [isFileLoading, setFileLoading] = useState(true)
  const [fileContent, setFileContent] = useState<string>('')
  const store = useStoreContext()
  const handleFileLoad = () => setFileLoading(false)

  useEffect(() => {
    const lowerFile = previewUrl?.toLowerCase() || ''
    const isCodeOrText =
      /\.(txt|log|md|xml|json|ts|tsx|js|jsx|py|java|html|css|sh|yml|yaml|csv)$/i.test(
        lowerFile
      )

    if (previewUrl) {
      setFileLoading(true)

      if (isCodeOrText) {
        fetch(previewUrl)
          .then((res) => res.text())
          .then((text) => {
            setFileContent(text)
            setFileLoading(false)
          })
          .catch((err) => {
            setFileLoading(false)
          })
      } else {
        setFileContent('')
        setTimeout(() => setFileLoading(false), 300)
      }
    } else {
      setFileContent('')
      setFileLoading(false)
    }
  }, [previewUrl])

  const handlePreview = (url: string) => {
    setPreviewUrl(url)
    store.uiStore.setSidebarExpanded(false)
  }

  const closeModal = () => {
    setPreviewUrl(null)
    store.uiStore.setSidebarExpanded(true)
  }

  const fileTypes: Record<string, { icon: JSX.Element; label: string }> = {
    'application/pdf': {
      icon: (
        <FaFilePdf
          style={{ color: pallete.colors.red500 }}
          className='text-2xl'
        />
      ),
      label: 'PDF Document'
    },
    'application/msword': {
      icon: (
        <FaFileWord
          style={{ color: pallete.colors.blue500 }}
          className='text-2xl'
        />
      ),
      label: 'Word Document'
    },
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': {
      icon: (
        <FaFileWord
          style={{ color: pallete.colors.blue500 }}
          className='text-2xl'
        />
      ),
      label: 'Word Document'
    },
    'application/vnd.ms-excel': {
      icon: (
        <FaFileExcel
          style={{ color: pallete.colors.green500 }}
          className='text-2xl'
        />
      ),
      label: 'Excel Spreadsheet'
    },
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': {
      icon: (
        <FaFileExcel
          style={{ color: pallete.colors.green500 }}
          className='text-2xl'
        />
      ),
      label: 'Excel Spreadsheet'
    },
    'text/csv': {
      icon: (
        <FaFileCsv
          style={{ color: pallete.colors.green500 }}
          className='text-2xl'
        />
      ),
      label: 'CSV File'
    },
    'application/vnd.ms-powerpoint': {
      icon: (
        <FaFilePowerpoint
          style={{ color: pallete.colors.orange500 }}
          className='text-2xl'
        />
      ),
      label: 'PowerPoint Presentation'
    },
    'application/vnd.openxmlformats-officedocument.presentationml.presentation':
      {
        icon: (
          <FaFilePowerpoint
            style={{ color: pallete.colors.orange500 }}
            className='text-2xl'
          />
        ),
        label: 'PowerPoint Presentation'
      },
    'application/json': {
      icon: (
        <FaFileCode
          style={{ color: pallete.colors.yellow500 }}
          className='text-2xl'
        />
      ),
      label: 'JSON File'
    },
    'text/plain': {
      icon: (
        <FaFileAlt
          style={{ color: pallete.colors.gray500 }}
          className='text-2xl'
        />
      ),
      label: 'Text File'
    },
    'text/xml': {
      icon: (
        <FaFileCode
          style={{ color: pallete.colors.blue400 }}
          className='text-2xl'
        />
      ),
      label: 'XML File'
    },
    'text/markdown': {
      icon: (
        <FaFileAlt
          style={{ color: pallete.colors.gray500 }}
          className='text-2xl'
        />
      ),
      label: 'Markdown File'
    },
    'application/x-sh': {
      icon: (
        <FaFileCode
          style={{ color: pallete.colors.gray500 }}
          className='text-2xl'
        />
      ),
      label: 'Shell Script'
    },
    'application/zip': {
      icon: (
        <FaFileArchive
          style={{ color: pallete.colors.purple500 }}
          className='text-2xl'
        />
      ),
      label: 'ZIP Archive'
    },
    'image/jpeg': {
      icon: (
        <FaFileImage
          style={{ color: pallete.colors.pink500 }}
          className='text-2xl'
        />
      ),
      label: 'Image (JPG)'
    },
    'image/png': {
      icon: (
        <FaFileImage
          style={{ color: pallete.colors.pink500 }}
          className='text-2xl'
        />
      ),
      label: 'Image (PNG)'
    },
    'image/webp': {
      icon: (
        <FaFileImage
          style={{ color: pallete.colors.pink500 }}
          className='text-2xl'
        />
      ),
      label: 'Image (WebP)'
    },
    'image/gif': {
      icon: (
        <FaFileImage
          style={{ color: pallete.colors.pink500 }}
          className='text-2xl'
        />
      ),
      label: 'Image (GIF)'
    },
    'video/mp4': {
      icon: (
        <FaFileVideo
          style={{ color: pallete.colors.indigo500 }}
          className='text-2xl'
        />
      ),
      label: 'Video (MP4)'
    },
    'video/webm': {
      icon: (
        <FaFileVideo
          style={{ color: pallete.colors.indigo500 }}
          className='text-2xl'
        />
      ),
      label: 'Video (WebM)'
    },
    'video/quicktime': {
      icon: (
        <FaFileVideo
          style={{ color: pallete.colors.indigo500 }}
          className='text-2xl'
        />
      ),
      label: 'Video (MOV)'
    },
    'audio/mpeg': {
      icon: (
        <FaFileAudio
          style={{ color: pallete.colors.green700 }}
          className='text-2xl'
        />
      ),
      label: 'Audio (MP3)'
    },
    'application/x-apple-diskimage': {
      icon: (
        <FaFileArchive
          style={{ color: pallete.colors.purple500 }}
          className='text-2xl'
        />
      ),
      label: 'DMG Disk Image'
    }
  }

  const fileType = fileTypes[file.type] || {
    icon: <FaFileAlt className='text-gray-400 text-2xl' />,
    label: 'Unknown File'
  }

  const fileName = file?.name?.toLowerCase() || ''
  const fileTypeSupport = file?.type || ''

  const isCode = /\.(json|ts|tsx|js|jsx|py|java|html|css|sh|yml|yaml)$/i.test(
    fileName
  )
  const isOffice = /\.(doc|docx|xls|xlsx|ppt|pptx)$/i.test(fileName)
  const isCSV = /\.csv$/i.test(fileName)

  const isImage =
    fileTypeSupport.includes('image/') && !fileTypeSupport.includes('heic')
  const isVideo = fileTypeSupport.includes('video')
  const isPdf = fileTypeSupport === 'application/pdf'
  const isDocx = fileTypeSupport.includes('wordprocessingml.document')
  const isPptx = fileTypeSupport.includes('presentationml.presentation')
  const isTextBased =
    fileTypeSupport.startsWith('text/') || fileTypeSupport.includes('json')

  const previewSupported =
    isImage ||
    isVideo ||
    isPdf ||
    isDocx ||
    isPptx ||
    isTextBased ||
    isCode ||
    isOffice ||
    isCSV

  return (
    <div>
      {uploadType === 'create' ? (
        <div
          style={{ backgroundColor: pallete.colors.surface6 }}
          className={`flex items-center w-[196px] h-[60px]  rounded-md p-[8px] gap-[4px] relative group`}
        >
          <button
            style={{
              backgroundColor: pallete.colors.lightWhite,
              borderColor: pallete.colors.borderWhite
            }}
            className='absolute -top-[3px] -right-[3px] w-[24px] h-[24px] rounded-full  text-white opacity-0 group-hover:opacity-100 flex items-center justify-center transition-all duration-200 border-[2px] '
            onClick={onClose}
          >
            <Close size={12} />
          </button>

          <div className='w-1/4 flex justify-between'>{fileType.icon}</div>

          <div className='w-2/4 flex flex-col justify-between'>
            <DDSTypography.Paragraph size='para' variant='medium' color='black'>
              {file.name.slice(0, 10)}...
            </DDSTypography.Paragraph>
            <DDSTypography.Paragraph
              size='caption'
              variant='medium'
              color='black'
            >
              {file.size}
            </DDSTypography.Paragraph>
          </div>
        </div>
      ) : uploadType === 'delete' ? (
        <div
          style={{ backgroundColor: pallete.colors.surface6 }}
          className='relative flex items-center w-[200px] h-[62px]  rounded-md p-[10px_12px_10px_8px] gap-[10px] group'
        >
          <div className='absolute top-1 right-1 flex gap-[4px] z-10'></div>

          <div className='w-[42px] h-[42px] flex items-center justify-center'>
            {fileType.icon}
          </div>

          <div className='flex flex-col justify-center'>
            <Tooltip
              id={`tooltip-name-${file.name}`}
              label={file.name}
              className={`override-tooltip-arrow ${
                typeof file.name === 'string' && file.name.length > 10
                  ? ''
                  : '!hidden'
              }`}
            >
              <div>
                <DDSTypography.Paragraph
                  size='para'
                  variant='medium'
                  color={pallete.colors.textDark4}
                  className='tooltip-container sm:block overflow-hidden truncate max-w-[90px]'
                >
                  {file.name || '-'}
                </DDSTypography.Paragraph>
              </div>
            </Tooltip>
            <DDSTypography.Paragraph
              size='caption'
              variant='medium'
              color={pallete.colors.textDark6}
            >
              {file.size}
            </DDSTypography.Paragraph>
          </div>
        </div>
      ) : (
        <div
          className='relative flex items-center w-[200px] h-[62px]  rounded-md p-[10px_12px_10px_8px] gap-[10px] group'
          style={{ backgroundColor: pallete.colors.surface6 }}
        >
          <div className='absolute top-1 right-1 flex gap-[4px] z-10'>
            {!download && previewSupported && (
              <button
                style={{ backgroundColor: pallete.colors.lightWhite }}
                className='text-white p-[4px] rounded-full hover:opacity-80 transition'
                onClick={() => handlePreview(file.url)}
              >
                <FitToScreen size={12} />
              </button>
            )}

            <a
              href={file.url}
              download
              target='_blank'
              rel='noopener noreferrer'
              style={{ backgroundColor: pallete.colors.lightWhite }}
              className='text-white p-[4px] rounded-full hover:opacity-80 transition'
            >
              <Download size={12} />
            </a>
          </div>

          <div className='w-[42px] h-[42px] flex items-center justify-center'>
            {fileType.icon}
          </div>

          <div className='flex flex-col justify-center'>
            <Tooltip
              id={`tooltip-name-${file.name}`}
              label={file.name}
              className={`override-tooltip-arrow ${
                typeof file.name === 'string' && file.name.length > 10
                  ? ''
                  : '!hidden'
              }`}
            >
              <div>
                <DDSTypography.Paragraph
                  size='para'
                  variant='medium'
                  color={pallete.colors.textDark4}
                  className='tooltip-container sm:block overflow-hidden truncate max-w-[90px]'
                >
                  {file.name || '-'}
                </DDSTypography.Paragraph>
              </div>
            </Tooltip>
            <DDSTypography.Paragraph
              size='caption'
              variant='medium'
              color={pallete.colors.textDark6}
            >
              {file.size}
            </DDSTypography.Paragraph>
          </div>
        </div>
      )}

      <DocumentPreviewModal
        previewUrl={previewUrl}
        isFileLoading={isFileLoading}
        fileContent={fileContent}
        handleFileLoad={handleFileLoad}
        closeModal={closeModal}
      />
    </div>
  )
}

export default FilePreviewCard
